import { type RefObject, useEffect, useRef } from 'react';

/** Delay before the first request, so a page that is still hydrating settles. */
const START_DELAY_MS = 2000;
const RETRY_DELAYS_MS = [3000, 10000];
/** Re-check this long after a page was appended, by which time it is laid out. */
const SETTLE_MS = 300;

export type LoadMore = () => Promise<unknown> | void;

/**
 * Infinite-scroll sentinel: requests the next page whenever the element comes
 * within one viewport of the visible area, and keeps requesting while it stays
 * there.
 *
 * Measured on every scroll rather than watched with an IntersectionObserver.
 * An observer reports transitions, and the two states that matter here produce
 * none: a reader standing at the bottom keeps the sentinel at a fixed position
 * while pages are inserted above it (scroll anchoring), and a reader who
 * scrolled past it into the footer leaves it above the viewport for good. The
 * desktop layout also scrolls `#content` rather than the document, so the
 * geometry an observer would need cannot be derived from `window.scrollY`.
 */
export const useLoadMoreSentinel = <T extends HTMLElement>(
  loadMore: LoadMore,
): RefObject<T> => {
  const ref = useRef<T>(null);

  useEffect(() => {
    let disposed = false;
    let inFlight = false;
    let failures = 0;
    let started = false;
    let frame = 0;
    /** Nothing is requested before this moment, so a failing backend is not hammered. */
    let blockedUntil = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Whether the end of the list is close enough to ask for more. Anything
     * above the viewport counts: the reader is already past the sentinel.
     */
    const isNear = () => {
      const element = ref.current;

      return (
        !!element &&
        element.isConnected &&
        element.getBoundingClientRect().top < window.innerHeight * 2
      );
    };

    const check = () => {
      if (
        disposed ||
        !started ||
        inFlight ||
        Date.now() < blockedUntil ||
        !isNear()
      ) {
        return;
      }

      inFlight = true;

      Promise.resolve()
        .then(loadMore)
        .then(
          () => {
            inFlight = false;
            failures = 0;
            blockedUntil = 0;
            settleTimer = setTimeout(check, SETTLE_MS);
          },
          () => {
            inFlight = false;

            const scheduled = RETRY_DELAYS_MS[failures];
            // Past the scheduled retries the last delay becomes the minimum gap
            // between attempts, so a scroll can still revive a recovered
            // backend without turning into one request per gesture.
            const delay =
              scheduled ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] ?? 0;

            failures += 1;
            blockedUntil = Date.now() + delay;

            if (scheduled !== undefined) {
              retryTimer = setTimeout(check, scheduled);
            }
          },
        );
    };

    const schedule = () => {
      if (frame || disposed) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = 0;
        check();
      });
    };

    const startTimer = setTimeout(() => {
      started = true;
      check();
    }, START_DELAY_MS);

    // Capture phase, because the scroller is `#content` on desktop and the
    // document on mobile, and scroll events do not bubble.
    window.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', schedule, { passive: true });

    const container = ref.current?.parentElement;
    const resizeObserver = container ? new ResizeObserver(schedule) : null;

    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    return () => {
      disposed = true;
      clearTimeout(startTimer);
      clearTimeout(retryTimer);
      clearTimeout(settleTimer);
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [loadMore]);

  return ref;
};
